import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { PageContent } from '../types';
import { supabase } from '../lib/supabase';

interface PageContentContextType {
  pageContents: PageContent[];
  loading: boolean;
  error: string | null;
  getPageContent: (pageKey: 'greeting' | 'about_gtc' | 'curriculum' | 'facilities' | 'location' | 'programs' | 'notice' | 'gallery' |
                            'program_group' | 'program_preschool' | 'program_elementary' | 'program_secondary' |
                            'program_adult' | 'program_special' | 'program_camp' | 'program_international') => PageContent | undefined;
  updatePageContent: (pageKey: string, data: Partial<PageContent>) => Promise<void>;
  refreshPageContents: () => Promise<void>;
}

const PageContentContext = createContext<PageContentContextType | undefined>(undefined);

export const PageContentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pageContents, setPageContents] = useState<PageContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Supabase에서 페이지 콘텐츠 데이터 가져오기
  const fetchPageContents = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('page_contents')
        .select('*')
        .order('page_key', { ascending: true });

      if (fetchError) throw fetchError;

      setPageContents(data || []);
    } catch (err) {
      console.error('Error fetching page contents:', err);
      setError(err instanceof Error ? err.message : '페이지 콘텐츠를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchPageContents();
  }, []);

  // 특정 페이지 콘텐츠 가져오기
  const getPageContent = (pageKey: 'greeting' | 'about_gtc' | 'curriculum' | 'facilities' | 'location' | 'programs' | 'notice' | 'gallery' |
                                   'program_group' | 'program_preschool' | 'program_elementary' | 'program_secondary' |
                                   'program_adult' | 'program_special' | 'program_camp' | 'program_international') => {
    return pageContents.find(content => content.page_key === pageKey);
  };

  // 페이지 콘텐츠 업데이트 (INSERT or UPDATE)
  const updatePageContent = async (pageKey: string, data: Partial<PageContent>) => {
    try {
      setError(null);

      const updateData: any = { page_key: pageKey };
      if (data.title_main !== undefined) updateData.title_main = data.title_main;
      if (data.title_sub !== undefined) updateData.title_sub = data.title_sub;
      if (data.title_eng !== undefined) updateData.title_eng = data.title_eng;
      if (data.description !== undefined) updateData.description = data.description;
      if (data.image_url !== undefined) updateData.image_url = data.image_url;

      // UPSERT (INSERT or UPDATE)
      const { error: upsertError } = await supabase
        .from('page_contents')
        .upsert(updateData, { onConflict: 'page_key' });

      if (upsertError) throw upsertError;

      // 데이터 다시 불러오기
      await fetchPageContents();
    } catch (err) {
      console.error('Error updating page content:', err);
      setError(err instanceof Error ? err.message : '페이지 콘텐츠 수정에 실패했습니다.');
      throw err;
    }
  };

  const refreshPageContents = async () => {
    await fetchPageContents();
  };

  return (
    <PageContentContext.Provider value={{
      pageContents,
      loading,
      error,
      getPageContent,
      updatePageContent,
      refreshPageContents
    }}>
      {children}
    </PageContentContext.Provider>
  );
};

export const usePageContents = () => {
  const context = useContext(PageContentContext);
  if (context === undefined) {
    throw new Error('usePageContents must be used within a PageContentProvider');
  }
  return context;
};
